import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch } from "../../../core/data/redux/store";
import { getDarAppeal, createDarAppeal, updateDarAppeal, uploadDarAppealOrder, getDarClosed, getDarPending, getDarCharges } from "../../../core/data/redux/darSlice";
import { getPresignedUrl, uploadFile } from "../../../core/data/redux/awsBucketSlice";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import axios from "axios";

const DarAppeal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const casenoParam = queryParams.get("caseno");
  const isCreate = !casenoParam;
  const todayStr = new Date().toISOString().split("T")[0];

  const [searchCaseNo, setSearchCaseNo] = useState(casenoParam || "");
  const [formData, setFormData] = useState({
    caseno: casenoParam || "",
    empno: "",
    empname: "",
    penalty_type: "",
    penalty_start_date: "",
    penalty_desc: "",
    charges: [] as number[],
    chargesheetdate: "",
    chargesheetfile: "",
    chargesheet_url: "",
    aaname: "",
    aadesignation: "",
    aaempno: "",
    appeal_date: "",
    aa_decision: "",
    aa_orderfile: "",
    corrigendumfile: "",
    status: "",
    aa_order_url: "",
  });

  const [aaOrderFileObj, setAaOrderFileObj] = useState<File | null>(null);
  const [corrigendumFileObj, setCorrigendumFileObj] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingOrder, setUploadingOrder] = useState(false);
  const [uploadingCorrigendum, setUploadingCorrigendum] = useState(false);
  const [chargeOptions, setChargeOptions] = useState<any[]>([]);

  const getChargeIds = (data: any): number[] => {
    const charges = Array.isArray(data?.charges)
      ? data.charges
      : Array.isArray(data?.charge_ids)
        ? data.charge_ids
        : [];

    return charges
      .map((charge: any) => Number(typeof charge === "object" ? charge.id : charge))
      .filter((id: number) => Number.isFinite(id));
  };

  useEffect(() => {
    dispatch(getDarCharges({}))
      .unwrap()
      .then((response: any) => {
        const data = response?.data?.data ?? response?.data ?? response;
        setChargeOptions(Array.isArray(data) ? data : []);
      })
      .catch((error: any) => console.error("Failed to fetch DAR charges", error));
  }, [dispatch]);

  useEffect(() => {
    if (casenoParam) {
      handleFetchAppeal(casenoParam);
    }
  }, [casenoParam]);

  const handleFetchAppeal = async (caseno: string) => {
    setLoading(true);
    try {
      const response = await dispatch(getDarAppeal({ caseno })).unwrap();
      const status = response?.status || response?.data?.status;
      if ((status === 200 || status === 201) && response?.data) {
        const data = response.data?.data || response.data;

        const closedObj = data.closedDetails || data.closed_details || data.closedCase || data.closed_case || {};
        const pendingObj = data.pendingDetails || data.pending_details || data.pendingCase || data.pending_case || {};

        let closedData: any = {};
        let pendingData: any = {};

        const hasPenalty = Boolean(data.penalty_type || closedObj.penalty_type);
        const hasChargesheet = Boolean(data.chargesheetdate || data.chargesheet_date || closedObj.chargesheetdate || pendingObj.chargesheetdate);

        if (!hasPenalty || !hasChargesheet) {
          try {
            const closedRes = await dispatch(getDarClosed({ caseno })).unwrap();
            if (closedRes?.data) {
              closedData = closedRes.data?.data || closedRes.data;
            }
          } catch (e) {
            console.error("Optional fetch of getDarClosed failed:", e);
          }

          try {
            const pendingRes = await dispatch(getDarPending({ caseno })).unwrap();
            if (pendingRes?.data) {
              pendingData = pendingRes.data?.data || pendingRes.data;
            }
          } catch (e) {
            console.error("Optional fetch of getDarPending failed:", e);
          }
        }

        const penalty_type =
          data.penalty_type ||
          closedObj.penalty_type ||
          closedData.penalty_type ||
          "";

        const penalty_start_date =
          data.penalty_start_date ||
          closedObj.penalty_start_date ||
          closedData.penalty_start_date ||
          "";

        const penalty_desc =
          data.penalty_desc ||
          closedObj.penalty_desc ||
          closedData.penalty_desc ||
          "";

        const chargesheetdate =
          data.chargesheetdate ||
          data.chargesheet_date ||
          closedObj.chargesheetdate ||
          closedObj.chargesheet_date ||
          pendingObj.chargesheetdate ||
          pendingObj.chargesheet_date ||
          closedData.chargesheetdate ||
          pendingData.chargesheetdate ||
          "";

        const chargesheetfile =
          data.chargesheetfile ||
          data.chargesheet_file ||
          closedObj.chargesheetfile ||
          closedObj.chargesheet_file ||
          pendingObj.chargesheetfile ||
          pendingObj.chargesheet_file ||
          closedData.chargesheetfile ||
          pendingData.chargesheetfile ||
          "";

        const chargesheet_url =
          data.urls?.chargesheet_url ||
          data.chargesheet_url ||
          closedObj.urls?.chargesheet_url ||
          closedObj.chargesheet_url ||
          pendingObj.urls?.chargesheet_url ||
          pendingObj.chargesheet_url ||
          closedData.urls?.chargesheet_url ||
          closedData.chargesheet_url ||
          pendingData.urls?.chargesheet_url ||
          pendingData.chargesheet_url ||
          "";

        const charges =
          getChargeIds(data).length > 0
            ? getChargeIds(data)
            : getChargeIds(closedObj).length > 0
              ? getChargeIds(closedObj)
              : getChargeIds(pendingObj).length > 0
                ? getChargeIds(pendingObj)
                : getChargeIds(closedData).length > 0
                  ? getChargeIds(closedData)
                  : getChargeIds(pendingData);

        setFormData({
          caseno: data.caseno || caseno,
          empno: data.empno || closedObj.empno || closedData.empno || pendingData.empno || "",
          empname: data.empname || closedObj.empname || closedData.empname || pendingData.empname || "",
          penalty_type,
          penalty_start_date,
          penalty_desc,
          charges,
          chargesheetdate,
          chargesheetfile,
          chargesheet_url,
          aaname: data.aaname || data.aa_name || closedObj.aa_name || closedObj.aaname || "",
          aadesignation: data.aadesignation || data.aa_designation || closedObj.aa_designation || closedObj.aadesignation || "",
          aaempno: String(data.aaempno || data.aa_empno || closedObj.aa_empno || closedObj.aaempno || ""),
          appeal_date: data.appeal_date || "",
          aa_decision: data.aa_decision || "",
          aa_orderfile: data.aa_orderfile || "",
          corrigendumfile: data.corrigendumfile || "",
          status: data.status || "",
          aa_order_url: data.urls?.aa_order_url || data.url || "",
        });

        // Dynamic fetching for docs if only key is returned
        if (data.aa_orderfile && !data.urls?.aa_order_url && !data.url) {
          dispatch(getPresignedUrl(data.aa_orderfile))
            .unwrap()
            .then((url: any) => setFormData((prev) => ({ ...prev, aa_order_url: url as string })))
            .catch((err: any) => console.error("Failed to fetch order presigned URL", err));
        }

        if (chargesheetfile && !chargesheet_url) {
          dispatch(getPresignedUrl(chargesheetfile))
            .unwrap()
            .then((url: any) => setFormData((prev) => ({ ...prev, chargesheet_url: url as string })))
            .catch((err: any) => console.error("Failed to fetch charge-sheet document URL", err));
        }
      } else {
        toast.error(response?.message || response?.data?.message || "Failed to fetch appeal case details.");
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error fetching appeal case details."));
    } finally {
      setLoading(false);
    }
  };

  const handleFetchClosed = async () => {
    if (!searchCaseNo.trim()) {
      toast.warning("Please enter a Case Number.");
      return;
    }
    setLoading(true);
    try {
      const response = await dispatch(getDarClosed({ caseno: searchCaseNo.trim() })).unwrap();
      const status = response?.status || response?.data?.status;
      if ((status === 200 || status === 201) && response?.data) {
        const data = response.data?.data || response.data;
        let pendingData: any = {};

        try {
          const pendingResponse = await dispatch(getDarPending({ caseno: data.caseno || searchCaseNo.trim() })).unwrap();
          pendingData = pendingResponse?.data?.data || pendingResponse?.data || {};
        } catch (error) {
          console.error("Failed to fetch inherited pending-case details", error);
        }

        const charges = getChargeIds(data).length > 0 ? getChargeIds(data) : getChargeIds(pendingData);
        const chargesheetfile = data.chargesheetfile || pendingData.chargesheetfile || "";
        const chargesheetUrl =
          data.urls?.chargesheet_url ||
          data.chargesheet_url ||
          pendingData.urls?.chargesheet_url ||
          pendingData.chargesheet_url ||
          "";
        setFormData(prev => ({
          ...prev,
          caseno: data.caseno || "",
          empno: data.empno || "",
          empname: data.empname || "",
          penalty_type: data.penalty_type || "",
          penalty_start_date: data.penalty_start_date || "",
          penalty_desc: data.penalty_desc || "",
          charges,
          chargesheetdate: data.chargesheetdate || pendingData.chargesheetdate || "",
          chargesheetfile,
          chargesheet_url: chargesheetUrl,
          aaname: data.aa_name || data.aaname || pendingData.aa_name || pendingData.aaname || "",
          aadesignation: data.aa_designation || data.aadesignation || pendingData.aa_designation || pendingData.aadesignation || "",
          aaempno: String(data.aa_empno || data.aaempno || pendingData.aa_empno || pendingData.aaempno || ""),
        }));

        if (chargesheetfile && !chargesheetUrl) {
          dispatch(getPresignedUrl(chargesheetfile))
            .unwrap()
            .then((url: any) => setFormData(prev => ({ ...prev, chargesheet_url: url as string })))
            .catch((error: any) => console.error("Failed to fetch charge-sheet document URL", error));
        }
      } else {
        toast.error(response?.message || response?.data?.message || "Pending details not found for this case.");
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error fetching closed case."));
    } finally {
      setLoading(false);
    }
  };

  const uploadToS3 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = (reader.result as string).split(",")[1];
        try {
          const result = await dispatch(
            uploadFile({ filestring: base64, mime_type: file.type, module: "dar" })
          );
          if (uploadFile.fulfilled.match(result)) {
            const key = result.payload?.key || result.payload?.data?.key || result.payload?.url || "";
            if (key) resolve(key);
            else reject(new Error("Failed to retrieve document path"));
          } else {
            reject(new Error("Failed to upload document"));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, targetField: "aa_orderfile" | "corrigendumfile") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      if (e.target) e.target.value = "";
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Invalid file format. Only PDF files are allowed.");
      if (e.target) e.target.value = "";
      return;
    }

    const setUploading = targetField === "aa_orderfile" ? setUploadingOrder : setUploadingCorrigendum;
    setUploading(true);
    const toastId = toast.loading("Uploading document...");
    try {
      const key = await uploadToS3(file);
      setFormData(prev => ({ ...prev, [targetField]: key }));

      if (formData.caseno) {
        try {
          await dispatch(uploadDarAppealOrder({
            caseno: formData.caseno,
            [targetField]: key
          })).unwrap();
        } catch (apiErr) {
          console.error("uploadDarAppealOrder API call error:", apiErr);
        }
      }

      toast.dismiss(toastId);
      toast.success("Document uploaded successfully");
      if (targetField === "aa_orderfile") {
        setAaOrderFileObj(file);
      } else {
        setCorrigendumFileObj(file);
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to upload document");
    } finally {
      e.target.value = ''; // Reset input
      setUploading(false);
    }
  };

  const handleRemoveFile = (targetField: "aa_orderfile" | "corrigendumfile") => {
    setFormData(prev => ({ ...prev, [targetField]: "" }));
    if (targetField === "aa_orderfile") {
      setAaOrderFileObj(null);
    } else {
      setCorrigendumFileObj(null);
    }
  };

  const handleViewFile = async (fileKey: string, existingUrl?: string) => {
    if (existingUrl) {
      window.open(existingUrl, "_blank");
      return;
    }
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileKey)).unwrap();
      toast.dismiss(toastId);
      window.open(url, "_blank");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.caseno) {
      toast.error("Case Number is missing. Please fetch closed case details first.");
      return;
    }
    if (!formData.aaname || !formData.aadesignation || !formData.appeal_date || !formData.aa_decision) {
      toast.error("Please fill all mandatory fields.");
      return;
    }
    if (isCreate && !aaOrderFileObj && !formData.aa_orderfile) {
      toast.error("Please attach the Appellate Authority order document.");
      return;
    }

    setSubmitting(true);
    try {
      let orderDocName = formData.aa_orderfile;

      if (isCreate) {
        const payload: any = {
          caseno: formData.caseno,
          aa_name: formData.aaname,
          aa_designation: formData.aadesignation,
          appeal_date: formData.appeal_date,
          aa_decision: formData.aa_decision,
          aa_orderfile: orderDocName
        };
        if (formData.aaempno) {
          const digitsOnly = formData.aaempno.replace(/\D/g, '');
          if (digitsOnly) {
            payload.aa_empno = digitsOnly;
          }
        }
        const response = await dispatch(createDarAppeal(payload)).unwrap();
        const resData = response?.data || response;
        const isSuccess =
          response?.success === true ||
          resData?.success === true ||
          response?.status === 200 ||
          response?.status === 201 ||
          resData?.status === 200 ||
          resData?.status === 201 ||
          response?.statusCode === 200 ||
          resData?.statusCode === 200 ||
          !!resData?.appealDetails ||
          !!response?.appealDetails;

        const message =
          response?.message ||
          resData?.message ||
          response?.data?.message ||
          "Appeal processed successfully. Case status updated to Appeal Closed.";

        if (isSuccess) {
          toast.success(message);
          navigate("/dar-cases/appeal-against-penalty");
        } else {
          toast.error(message || "Failed to process appeal.");
        }
      } else {
        const payload: any = {
          caseno: formData.caseno,
          aa_name: formData.aaname,
          aa_designation: formData.aadesignation,
          appeal_date: formData.appeal_date,
          aa_decision: formData.aa_decision,
        };
        if (formData.aaempno) {
          const digitsOnly = formData.aaempno.replace(/\D/g, '');
          if (digitsOnly) {
            payload.aa_empno = digitsOnly;
          }
        }
        const response = await dispatch(updateDarAppeal(payload)).unwrap();
        const resData = response?.data || response;
        const isSuccess =
          response?.success === true ||
          resData?.success === true ||
          response?.status === 200 ||
          response?.status === 201 ||
          resData?.status === 200 ||
          resData?.status === 201 ||
          response?.statusCode === 200 ||
          resData?.statusCode === 200 ||
          !!resData?.appealDetails ||
          !!response?.appealDetails;

        const message =
          response?.message ||
          resData?.message ||
          response?.data?.message ||
          "Appeal case updated successfully!";

        if (isSuccess) {
          if (formData.corrigendumfile || formData.aa_orderfile) {
            try {
              await dispatch(uploadDarAppealOrder({
                caseno: formData.caseno,
                ...(formData.corrigendumfile ? { corrigendumfile: formData.corrigendumfile } : {}),
                ...(formData.aa_orderfile ? { aa_orderfile: formData.aa_orderfile } : {})
              })).unwrap();
            } catch (apiErr) {
              console.error("uploadDarAppealOrder API submit error:", apiErr);
            }
            toast.success("Appeal case and document updated successfully!");
          } else {
            toast.success(message);
          }
          navigate("/dar-cases/appeal-against-penalty");
        } else {
          toast.error(message || "Failed to update appeal case.");
        }
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error submitting appeal."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{isCreate ? "New Appeal" : "Edit Appeal"}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/dar-cases/appeal-against-penalty">D&AR Appeals</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{isCreate ? "New Appeal" : "Edit"}</li>
              </ol>
            </nav>
          </div>
        </div>

        {isCreate && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '8px' }}>
            <div className="card-header bg-white border-bottom p-3">
              <h5 className="mb-0 fw-bold text-danger">Select Closed Case for Appeal</h5>
            </div>
            <div className="card-body p-4">
              <div className="row align-items-end">
                <div className="col-md-5">
                  <label className="form-label">Closed Case Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. DAR-2026-HR-007"
                    value={searchCaseNo}
                    onChange={(e) => setSearchCaseNo(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button className="btn btn-danger w-100" onClick={handleFetchClosed} disabled={loading}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="ti ti-search me-2"></i>}
                    Fetch Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(formData.caseno || !isCreate) && (
          <form onSubmit={handleSubmit}>
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '8px' }}>
              <div className="card-header bg-white border-bottom p-3">
                <h5 className="mb-0 fw-bold">Linked Closed Case (Read-Only)</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted">Case Number</label>
                    <input type="text" className="form-control bg-light" value={formData.caseno} readOnly />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted">Employee Name</label>
                    <input type="text" className="form-control bg-light" value={formData.empname} readOnly />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted">Employee No</label>
                    <input type="text" className="form-control bg-light" value={formData.empno} readOnly />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted">Penalty Type</label>
                    <input type="text" className="form-control bg-light" value={formData.penalty_type} readOnly />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted">Penalty Start Date</label>
                    <input type="text" className="form-control bg-light" value={formData.penalty_start_date ? new Date(formData.penalty_start_date).toLocaleDateString() : ""} readOnly />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label text-muted">Penalty Description</label>
                    <textarea className="form-control bg-light" rows={2} value={formData.penalty_desc} readOnly />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted">Charge Sheet Date</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={formData.chargesheetdate ? new Date(formData.chargesheetdate).toLocaleDateString() : ""}
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted">Charge Sheet Document</label>
                    <div className="form-control bg-light">
                      {formData.chargesheetfile || formData.chargesheet_url ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary text-decoration-none"
                          onClick={() => handleViewFile(formData.chargesheetfile, formData.chargesheet_url)}
                        >
                          <i className="ti ti-eye me-1"></i> View Document
                        </button>
                      ) : (
                        <span className="text-muted">No document uploaded</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label text-muted">Charges</label>
                    {formData.charges.length > 0 ? (
                      <div className="list-group list-group-flush border rounded-3 overflow-hidden">
                        {formData.charges.map((chargeId, index) => {
                          const charge = chargeOptions.find((option: any) =>
                            Number(typeof option === "object" ? option.id : option) === chargeId
                          );
                          return (
                            <div key={chargeId} className="list-group-item bg-light d-flex align-items-start gap-2 py-2">
                              <span className="fw-bold text-secondary">{index + 1}.</span>
                              <span>{charge?.charge_description || charge?.name || `Charge ${chargeId}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="form-control bg-light text-muted">No charges added</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '8px' }}>
              <div className="card-header bg-white border-bottom p-3">
                <h5 className="mb-0 fw-bold">Appellate Authority Details</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">AA Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control bg-light" value={formData.aaname} readOnly required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">AA Designation <span className="text-danger">*</span></label>
                    <input type="text" className="form-control bg-light" value={formData.aadesignation} readOnly required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">AA Emp No</label>
                    <input type="text" className="form-control bg-light" value={formData.aaempno} readOnly />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Appeal Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={formData.appeal_date ? new Date(formData.appeal_date).toISOString().split('T')[0] : ""} onChange={e => setFormData({ ...formData, appeal_date: e.target.value })} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">AA Decision <span className="text-danger">*</span></label>
                    <select className="form-select" value={formData.aa_decision} onChange={e => setFormData({ ...formData, aa_decision: e.target.value })} required>
                      <option value="">Select Decision</option>
                      <option value="Confirmed">Confirmed (No Change)</option>
                      <option value="Reduced">Reduced</option>
                      <option value="Enhanced">Enhanced</option>
                      <option value="Set Aside">Set Aside (Clear Case)</option>
                      <option value="Remitted Back">Remitted Back</option>
                    </select>
                  </div>

                  {formData.aa_decision === "Remitted Back" && (
                    <div className="col-md-12 mt-3">
                      <div className="alert alert-danger border-danger bg-danger-transparent d-flex align-items-start gap-3 rounded-3 p-3">
                        <i className="ti ti-alert-triangle fs-4 text-danger mt-1"></i>
                        <div>
                          <strong>CRITICAL ROLLBACK TRIGGER:</strong>
                          <p className="mb-0 mt-1 small text-dark">
                            Selecting "Remitted Back" will terminate the current closure tokens, wipe active penalties, and dynamically shift this case history row straight back to an active Pending Stage state.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {isCreate ? (
                    <div className="col-md-12">
                      <label className="form-label">
                        Order of AA (PDF only) <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="file"
                          id="aaOrderDoc"
                          className="d-none"
                          accept=".pdf"
                          onChange={e => handleFileUpload(e, "aa_orderfile")}
                          disabled={uploadingOrder}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white"
                          onClick={() => document.getElementById("aaOrderDoc")?.click()}
                          disabled={uploadingOrder}
                        >
                          <i className="ti ti-upload"></i> {uploadingOrder ? "Uploading..." : "Upload Order File"}
                        </button>
                        {formData.aa_orderfile && !uploadingOrder && (
                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.aa_orderfile)} title="View File">
                              {formData.aa_orderfile.split('/').pop()}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-icon btn-outline-danger"
                              title="Remove File"
                              onClick={() => handleRemoveFile("aa_orderfile")}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="text-muted small mt-2">Only PDF files under 5MB are acceptable.</div>
                    </div>
                  ) : (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Attached Appellate Order</label>
                        <div className="d-flex align-items-center gap-3 mt-1">
                          <input
                            type="file"
                            id="aaOrderDoc"
                            className="d-none"
                            accept=".pdf"
                            onChange={e => handleFileUpload(e, "aa_orderfile")}
                            disabled={uploadingOrder}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white"
                            onClick={() => document.getElementById("aaOrderDoc")?.click()}
                            disabled={uploadingOrder}
                          >
                            <i className="ti ti-upload"></i> {uploadingOrder ? "Uploading..." : "Update Order File"}
                          </button>
                          {formData.aa_orderfile && !uploadingOrder && (
                            <div className="d-flex align-items-center gap-2">
                              <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.aa_orderfile, formData.aa_order_url)} title="View File">
                                {formData.aa_orderfile.split('/').pop()}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="text-muted small mt-2">Only PDF files under 5MB are acceptable.</div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Upload Corrigendum (Optional)</label>
                        <div className="d-flex align-items-center gap-3 mt-1">
                          <input
                            type="file"
                            id="corrigendumDoc"
                            className="d-none"
                            accept=".pdf"
                            onChange={e => handleFileUpload(e, "corrigendumfile")}
                            disabled={uploadingCorrigendum}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white"
                            onClick={() => document.getElementById("corrigendumDoc")?.click()}
                            disabled={uploadingCorrigendum}
                          >
                            <i className="ti ti-upload"></i> {uploadingCorrigendum ? "Uploading..." : "Upload Corrigendum"}
                          </button>
                          {formData.corrigendumfile && !uploadingCorrigendum && (
                            <div className="d-flex align-items-center gap-2">
                              <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.corrigendumfile)} title="View File">
                                {formData.corrigendumfile.split('/').pop()}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="text-muted small mt-2">Only PDF files under 5MB are acceptable.</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="card-footer bg-white border-top p-3 text-end">
                <button type="submit" className="btn btn-warning px-4 d-flex align-items-center justify-content-center text-dark" style={{ minWidth: '150px' }} disabled={submitting}>
                  {submitting ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    <><i className="ti ti-device-floppy me-2"></i> Save</>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DarAppeal;
